import { useEffect, useMemo } from "preact/hooks";
import {
  amountSelected,
  amountSelectionReset,
  amountSubmitted,
  amountStepReturned,
  cardStepReturned,
  cardStepStarted,
  checkoutContextEntered,
  customAmountChanged,
  customAmountTouched,
  paymentReady,
  paymentSucceeded,
} from "./checkoutSlice";
import {
  selectCanAttemptCheckout,
  selectCheckoutStep,
  selectCustomAmount,
  selectSelectedAmount,
  selectShowCustomAmountError,
} from "./checkoutSelectors";
import { getPaymentAmountSelection } from "./amountSelection";
import type { PaymentAmountKind } from "./amountSelection";
import type { PendingPayment } from "./checkoutTypes";
import type { PaymentSummary } from "./payment";
import type { Invoice } from "../../lib/gboModels";
import { getApiFailureMessage, useMakePaymentMutation } from "../../store/gboApi";
import type { PaymentScope } from "../../store/gboApi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";

export type CheckoutResource = {
  scope: PaymentScope;
  invoice?: Invoice;
  customerId: string;
  accountBalance: number;
  payment: PaymentSummary;
  payOnlineEnabled: boolean;
};

export function useCheckoutFlow(resource: CheckoutResource) {
  const dispatch = useAppDispatch();
  const step = useAppSelector(selectCheckoutStep);
  const selectedAmount = useAppSelector(selectSelectedAmount);
  const customAmount = useAppSelector(selectCustomAmount);
  const showCustomAmountError = useAppSelector(selectShowCustomAmountError);
  const paymentAmount = useMemo(
    () =>
      getPaymentAmountSelection(
        selectedAmount,
        customAmount,
        resource.invoice?.balance,
        resource.accountBalance,
      ),
    [customAmount, resource.accountBalance, resource.invoice?.balance, selectedAmount],
  );
  const canPay = selectCanAttemptCheckout(
    resource.payment,
    resource.payOnlineEnabled,
    resource.accountBalance,
    resource.invoice,
  );
  const contextKey = getCheckoutContextKey(resource.scope);
  const [makePayment, paymentMutation] = useMakePaymentMutation();
  const paymentError = paymentMutation.isError
    ? getApiFailureMessage(paymentMutation.error, "Unable to submit payment.")
    : null;

  useEffect(() => {
    dispatch(
      checkoutContextEntered({
        contextKey,
        invoiceBalance: resource.invoice?.balance,
        accountBalance: resource.accountBalance,
      }),
    );
  }, [contextKey, dispatch, resource.accountBalance, resource.invoice?.balance]);

  function submitAmount() {
    dispatch(amountSubmitted());
    if (!paymentAmount.customAmountError) dispatch(cardStepStarted(paymentAmount));
  }

  function acceptProviderPayment(pendingPayment: PendingPayment) {
    const cardPaymentAmount = step.type === "card" ? step.paymentAmount : paymentAmount;
    dispatch(paymentReady({ paymentAmount: cardPaymentAmount, pendingPayment }));
  }

  function resetAfterSuccess() {
    dispatch(
      amountSelectionReset({
        contextKey,
        invoiceBalance:
          step.type === "success"
            ? (step.success.invoice?.balance ?? resource.invoice?.balance)
            : resource.invoice?.balance,
        accountBalance:
          step.type === "success" ? step.success.accountBalance : resource.accountBalance,
      }),
    );
  }

  async function submitPayment() {
    if (step.type !== "confirm") return;

    try {
      const success = await makePayment({
        request: step.pendingPayment.request,
        scope: resource.scope,
      }).unwrap();
      dispatch(paymentSucceeded(success));
    } catch {
      return;
    }
  }

  return {
    canPay,
    contextKey,
    customAmount,
    paymentAmount,
    paymentSubmission: {
      error: paymentError,
      submitting: paymentMutation.isLoading,
    },
    selectedAmount,
    showCustomAmountError,
    step,
    actions: {
      acceptProviderPayment,
      changeCustomAmount: (amount: string) => dispatch(customAmountChanged(amount)),
      returnToAmount: () => dispatch(amountStepReturned()),
      returnToCard: () => dispatch(cardStepReturned()),
      resetAfterSuccess,
      selectAmount: (selected: PaymentAmountKind) => dispatch(amountSelected(selected)),
      submitAmount,
      submitPayment,
      touchCustomAmount: () => dispatch(customAmountTouched()),
    },
  };
}

function getCheckoutContextKey(scope: PaymentScope) {
  return `${scope.type}:${scope.type === "invoice" ? scope.invoiceCode : scope.customerCode}`;
}
